import React from 'react';
import { mergeStyleSets } from '@fluentui/react';
import { Table, User, LockSimple, EnvelopeSimple } from '@phosphor-icons/react';
import { getPhosphorIcon } from '../../utils/iconMap';
import type { DepartmentSummary } from '../../types/dataset.types';

interface Props {
  dept: DepartmentSummary;
  onClick: () => void;
}

const styles = mergeStyleSets({
  tile: {
    borderRadius: 4,
    padding: 22,
    background: '#FFFFFF',
    border: '1px solid #E1E1E1',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative' as const,
    overflow: 'hidden',
    selectors: {
      ':hover': {
        background: '#F8FDF8',
        borderColor: '#D0D0D0',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      },
    },
  },
  tileDisabled: {
    opacity: 0.5,
    cursor: 'default',
    selectors: {
      ':hover': {
        background: '#FFFFFF',
        borderColor: '#E1E1E1',
        transform: 'none',
        boxShadow: 'none',
      },
    },
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  name: {
    fontSize: 15,
    fontWeight: 700,
    color: '#232323',
    marginBottom: 6,
    lineHeight: '1.3',
  },
  desc: {
    fontSize: 12.5,
    color: '#484848',
    lineHeight: '1.6',
    marginBottom: 12,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap' as const,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.4px',
    border: '1px solid #EDEDED',
    background: '#F5F5F5',
    color: '#747474',
  },
  accessBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 600,
  },
  statChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    color: '#747474',
  },
  ownerChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    color: '#747474',
  },
  comingSoonBanner: {
    position: 'absolute' as const,
    top: 12,
    right: -28,
    transform: 'rotate(45deg)',
    background: '#F5F5F5',
    padding: '2px 32px',
    fontSize: 9,
    fontWeight: 700,
    color: '#747474',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.6px',
  },
  requestBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 600,
    background: '#d9f4d7',
    border: '1px solid rgba(46,135,61,0.3)',
    color: '#2e873d',
    cursor: 'pointer',
    transition: 'all 0.15s',
    selectors: {
      ':hover': {
        background: '#c4edc2',
        borderColor: '#2e873d',
      },
    },
  },
});

export default function DepartmentTile({ dept, onClick }: Props) {
  const isComingSoon = dept.status === 'coming_soon';
  const colorFaded = dept.color + '20'; // 12% alpha
  const isLocked = !dept.hasAccess && dept.status === 'active';

  const handleRequestAccess = (e: React.MouseEvent) => {
    e.stopPropagation();
    const subject = encodeURIComponent(`Request Access: ${dept.department} Dataset`);
    const body = encodeURIComponent(
      `Hi ${dept.owner},\n\nI would like to request access to the "${dept.department}" dataset in the Boroondara Enterprise Data Portal.\n\nPlease let me know the next steps.\n\nThank you.`
    );
    window.open(`mailto:${dept.ownerEmail}?subject=${subject}&body=${body}`, '_self');
  };

  return (
    <div
      className={`${styles.tile} ${isComingSoon ? styles.tileDisabled : ''}`}
      onClick={isComingSoon ? undefined : onClick}
      role={isComingSoon ? undefined : 'button'}
      tabIndex={isComingSoon ? undefined : 0}
      onKeyDown={isComingSoon ? undefined : (e) => { if (e.key === 'Enter') onClick(); }}
      style={{ borderLeft: `4px solid ${dept.color}` }}
    >
      {isComingSoon && <div className={styles.comingSoonBanner}>Soon</div>}

      <div
        className={styles.iconWrap}
        style={{ background: colorFaded, border: `1px solid ${dept.color}30` }}
      >
        {React.createElement(getPhosphorIcon(dept.icon), { size: 18, color: dept.color })}
      </div>

      <div className={styles.name}>{dept.department}</div>
      <div className={styles.desc}>{dept.description}</div>

      <div className={styles.footer}>
        {/* Classification badge */}
        <span className={styles.badge}>{dept.classification}</span>

        {/* Table count */}
        {dept.tableCount > 0 && (
          <span className={styles.statChip}>
            <Table size={11} />
            {dept.tableCount} table{dept.tableCount !== 1 ? 's' : ''}
          </span>
        )}

        {/* Row count */}
        {dept.totalRows > 0 && (
          <span className={styles.statChip}>
            {dept.totalRows.toLocaleString()} rows
          </span>
        )}

        {/* Owner */}
        <span className={styles.ownerChip}>
          <User size={11} />
          {dept.owner}
        </span>

        {/* Access pill */}
        {!isComingSoon && (
          <span
            className={styles.accessBadge}
            style={
              dept.hasAccess
                ? {
                    background: '#E8F5E9',
                    border: '1px solid rgba(16,124,16,0.3)',
                    color: '#107C10',
                  }
                : {
                    background: 'rgba(251,191,36,0.12)',
                    border: '1px solid rgba(251,191,36,0.3)',
                    color: '#9A7B00',
                  }
            }
          >
            <LockSimple size={10} weight={dept.hasAccess ? 'fill' : 'regular'} />
            {dept.hasAccess ? 'Access' : 'Locked'}
          </span>
        )}

        {/* Request Access button for locked datasets */}
        {isLocked && (
          <button
            className={styles.requestBtn}
            onClick={handleRequestAccess}
            title={`Request access from ${dept.owner}`}
          >
            <EnvelopeSimple size={10} />
            Request Access
          </button>
        )}
      </div>
    </div>
  );
}
